import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CompanyFormModal, type CompanyFormPrefill } from '@/components/companies/CompanyFormModal';
import {
  PiCardField,
  PiCardList,
  PiEmpty,
  PiError,
  PiLoading,
  PiSearch,
  PiSortHeader,
  PiTabs,
  PiToolbar,
  piCount,
} from '@/components/product-insights/pi-kit';
import { Badge, Button, ConfirmDialog, Table, TableWrap, TBody, TD, TH, THead, TR } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import { api } from '@/lib/api';
import type { Company, PlatformCompanyRow, PlatformCompanySuggestion } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import { LinkPlatformCompanyDialog, type LinkPlatformCompanyTarget } from './LinkPlatformCompanyDialog';
import { EntityTypeBadge } from './EntityTypeBadge';
import { mapperErrorMessage, productLabel, productList } from './mapper-ui';

/**
 * The Company mapper on Modules Catalog (owner 2026-10-25, M1–M8).
 *
 * Read FROM SEED EMERGE (owner 2026-10-25, "emerge companies mapped to seed b2c
 * companies"): one row per SEED EMERGE company, listing the SEED platform
 * companies (schools / universities / partners) mapped onto it — MANY platform
 * records → ONE company — and, while it has none, the platform records the
 * server suggests for it. *SUPERSEDED: one row per platform record with its
 * company.* The platform-record view survives as the "Unmapped SEED companies"
 * tab, where a record nobody matches can still get a new company (M4).
 *
 * - Grants (M8): rendering needs `product-insights.read`; link / unlink need
 *   `product-insights.update`; "Create company" additionally `companies.create`.
 * - Both lists are read whole (the companies list page by page) and joined
 *   here: a mapped record is `link.company.id`; a suggestion is an unmapped
 *   record whose `suggestions` name the company. Search is local, over both
 *   names.
 * - Tab key in the URL is `mapper` (`suggested` | `mapped` | `all` | `seed`;
 *   never rename). ABSENT = Suggested matches while any exist, else Mapped.
 * - Links are staff-confirmed: every write goes through
 *   `LinkPlatformCompanyDialog` (with its unticked rename offer) or the
 *   create-company flow, which is two steps with no transaction, each reported.
 */

type Tab = 'suggested' | 'mapped' | 'all' | 'seed';

const TAB_KEY = 'mapper';
const PAGE_STEP = 50;
const COMPANY_PAGE = 100;
const NO_ROWS: readonly PlatformCompanyRow[] = [];
const NO_COMPANIES: readonly Company[] = [];

function readTab(value: string | null): Tab | null {
  return value === 'suggested' || value === 'mapped' || value === 'all' || value === 'seed' ? value : null;
}

type Load =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; rows: PlatformCompanyRow[]; companies: Company[]; failedSources: string[] };

type CreateOutcome =
  | { tone: 'success'; text: string }
  | { tone: 'warning'; text: string; retry: { row: PlatformCompanyRow; company: Company } };

interface Suggested {
  row: PlatformCompanyRow;
  reason: PlatformCompanySuggestion['reason'];
  score: number;
}

/** One SEED EMERGE company with what is mapped onto it and what is suggested for it. */
interface CompanyEntry {
  company: Company;
  mapped: PlatformCompanyRow[];
  suggested: Suggested[];
}

const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);

function platformFacts(row: PlatformCompanyRow): string {
  return [row.countryCode, row.website].filter((part): part is string => part !== null && part !== '').join(' · ');
}

async function loadAllCompanies(signal: AbortSignal): Promise<Company[]> {
  const first = await api.listCompanies({ page: 1, perPage: COMPANY_PAGE }, signal);
  const rest = await Promise.all(
    Array.from({ length: Math.max(0, first.meta.totalPages - 1) }, (_, index) =>
      api.listCompanies({ page: index + 2, perPage: COMPANY_PAGE }, signal),
    ),
  );
  return [first, ...rest].flatMap((page) => page.data);
}

export function CompanyMapperPanel() {
  const { ability } = useAuth();
  if (!ability.canRead('product-insights')) return null;
  return <MapperContent />;
}

function MapperContent() {
  const { ability } = useAuth();
  const canEdit = ability.canUpdate('product-insights');
  const canCreateCompany = canEdit && ability.canCreate('companies');

  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = readTab(searchParams.get(TAB_KEY));

  const [search, setSearch] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const [fetched, setFetched] = useState<{ key: number; load: Load } | null>(null);
  const load: Load = fetched !== null && fetched.key === reloadToken ? fetched.load : { status: 'loading' };

  const [limit, setLimit] = useState(PAGE_STEP);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [linkTarget, setLinkTarget] = useState<LinkPlatformCompanyTarget | null>(null);
  const [unlinkTarget, setUnlinkTarget] = useState<PlatformCompanyRow | null>(null);
  const [unlinkBusy, setUnlinkBusy] = useState(false);
  const [unlinkError, setUnlinkError] = useState<string | null>(null);

  const [createFor, setCreateFor] = useState<PlatformCompanyRow | null>(null);
  const [createPrefill, setCreatePrefill] = useState<CompanyFormPrefill | null>(null);
  const [preparing, setPreparing] = useState<string | null>(null);
  const [linkingCreated, setLinkingCreated] = useState(false);
  const [outcome, setOutcome] = useState<CreateOutcome | null>(null);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    Promise.all([api.listPlatformCompanies({ status: 'all' }, signal), loadAllCompanies(signal)])
      .then(([directory, companies]) => {
        if (signal.aborted) return;
        setFetched({
          key: reloadToken,
          load: { status: 'ready', rows: directory.data, companies, failedSources: directory.meta.failedSources },
        });
      })
      .catch((error: unknown) => {
        if (signal.aborted) return;
        setFetched({
          key: reloadToken,
          load: { status: 'error', message: mapperErrorMessage(error, 'The Company mapper could not be loaded.') },
        });
      });
    return () => controller.abort();
  }, [reloadToken]);

  const ready = load.status === 'ready';
  const rows = load.status === 'ready' ? load.rows : NO_ROWS;
  const companies = load.status === 'ready' ? load.companies : NO_COMPANIES;

  const entries = useMemo(() => {
    const byId = new Map<number, CompanyEntry>();
    for (const company of companies) byId.set(company.id, { company, mapped: [], suggested: [] });
    for (const row of rows) {
      if (row.link !== null) {
        byId.get(row.link.company.id)?.mapped.push(row);
        continue;
      }
      // One suggestion per (record, company), domain over name, then score.
      const best = new Map<number, Suggested>();
      for (const suggestion of row.suggestions) {
        const seen = best.get(suggestion.company.id);
        const rank = (s: { reason: string; score: number }) => (s.reason === 'domain' ? 1000 : 0) + s.score;
        if (seen === undefined || rank(suggestion) > rank(seen)) {
          best.set(suggestion.company.id, { row, reason: suggestion.reason, score: suggestion.score });
        }
      }
      for (const [companyId, suggested] of best) byId.get(companyId)?.suggested.push(suggested);
    }
    for (const entry of byId.values()) {
      entry.mapped.sort(byName);
      entry.suggested.sort(
        (a, b) => (b.reason === 'domain' ? 1 : 0) - (a.reason === 'domain' ? 1 : 0) || b.score - a.score,
      );
    }
    return [...byId.values()];
  }, [companies, rows]);

  const unmappedRows = useMemo(() => rows.filter((row) => row.link === null), [rows]);
  const mappedEntries = entries.filter((entry) => entry.mapped.length > 0);
  const suggestedEntries = entries.filter((entry) => entry.mapped.length === 0 && entry.suggested.length > 0);

  const smartDefault: Tab = !ready || suggestedEntries.length > 0 ? 'suggested' : 'mapped';
  const tab: Tab = urlTab ?? smartDefault;

  const needle = search.trim().toLowerCase();

  const visibleEntries = useMemo(() => {
    const matches = (entry: CompanyEntry) =>
      needle === '' ||
      entry.company.name.toLowerCase().includes(needle) ||
      entry.mapped.some((row) => row.name.toLowerCase().includes(needle)) ||
      entry.suggested.some((s) => s.row.name.toLowerCase().includes(needle));
    const inTab = (entry: CompanyEntry) =>
      tab === 'mapped'
        ? entry.mapped.length > 0
        : tab === 'suggested'
          ? entry.mapped.length === 0 && entry.suggested.length > 0
          : true;
    const sorted = entries.filter((entry) => inTab(entry) && matches(entry)).sort((a, b) => byName(a.company, b.company));
    return sortDir === 'asc' ? sorted : sorted.reverse();
  }, [entries, tab, sortDir, needle]);

  const visibleSeed = useMemo(() => {
    const sorted = unmappedRows
      .filter((row) => needle === '' || row.name.toLowerCase().includes(needle))
      .sort(byName);
    return sortDir === 'asc' ? sorted : sorted.reverse();
  }, [unmappedRows, needle, sortDir]);

  const visibleCount = tab === 'seed' ? visibleSeed.length : visibleEntries.length;
  const shownEntries = visibleEntries.slice(0, limit);
  const shownSeed = visibleSeed.slice(0, limit);

  const setTab = (next: Tab) => {
    setLimit(PAGE_STEP);
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        if (next === smartDefault) params.delete(TAB_KEY);
        else params.set(TAB_KEY, next);
        return params;
      },
      { replace: true },
    );
  };

  const changeSearch = (value: string) => {
    setSearch(value);
    setLimit(PAGE_STEP);
  };

  const replaceRow = (next: PlatformCompanyRow) => {
    setFetched((current) =>
      current !== null && current.load.status === 'ready'
        ? {
            key: current.key,
            load: {
              ...current.load,
              rows: current.load.rows.map((row) => (row.platformCompanyId === next.platformCompanyId ? next : row)),
            },
          }
        : current,
    );
  };

  const addCompany = (company: Company) => {
    setFetched((current) =>
      current !== null && current.load.status === 'ready'
        ? { key: current.key, load: { ...current.load, companies: [...current.load.companies, company] } }
        : current,
    );
  };

  /* ── Unlink ── */

  const confirmUnlink = () => {
    const row = unlinkTarget;
    if (row === null) return;
    setUnlinkBusy(true);
    setUnlinkError(null);
    api
      .deletePlatformCompanyLink(row.platformCompanyId)
      .then(() => {
        if (!mounted.current) return;
        replaceRow({ ...row, link: null });
        setUnlinkTarget(null);
      })
      .catch((error: unknown) => {
        if (mounted.current) setUnlinkError(mapperErrorMessage(error, 'The link could not be removed.'));
      })
      .finally(() => {
        if (mounted.current) setUnlinkBusy(false);
      });
  };

  /* ── Create company from an unmapped SEED record, then link it ── */

  const startCreate = async (row: PlatformCompanyRow) => {
    if (preparing !== null) return;
    setOutcome(null);
    setPreparing(row.platformCompanyId);
    let countryId: number | null = null;
    if (row.countryCode !== null && row.countryCode !== '') {
      try {
        const countries = await api.listCountries({ perPage: 250 });
        const code = row.countryCode.trim().toUpperCase();
        countryId = countries.data.find((country) => (country.countryCode ?? '').trim().toUpperCase() === code)?.id ?? null;
      } catch {
        countryId = null;
      }
    }
    if (!mounted.current) return;
    const prefill: CompanyFormPrefill = {
      name: row.name,
      countryId,
      ...(row.website !== null && row.website !== '' ? { website: row.website } : {}),
    };
    setCreatePrefill(prefill);
    setCreateFor(row);
    setPreparing(null);
  };

  const linkCreated = (row: PlatformCompanyRow, company: Company) => {
    setLinkingCreated(true);
    setOutcome(null);
    addCompany(company);
    api
      .setPlatformCompanyLink(row.platformCompanyId, { companyId: company.id })
      .then((saved) => {
        if (!mounted.current) return;
        replaceRow(saved);
        setOutcome({ tone: 'success', text: `Created ${company.name} and mapped ${row.name} to it.` });
      })
      .catch((error: unknown) => {
        if (!mounted.current) return;
        setOutcome({
          tone: 'warning',
          text: `Created ${company.name}, but ${row.name} was not mapped to it: ${mapperErrorMessage(error, 'the link could not be saved.')}`,
          retry: { row, company },
        });
      })
      .finally(() => {
        if (mounted.current) setLinkingCreated(false);
      });
  };

  /* ── Cells ── */

  const platformName = (row: PlatformCompanyRow) => (
    <span className="inline-flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
      <span className="font-medium break-words text-fg">{row.name}</span>
      <EntityTypeBadge entityType={row.entityType} />
    </span>
  );

  const productsOf = (row: PlatformCompanyRow) =>
    row.products.length === 0 ? null : (
      <ul className="m-0 flex list-none flex-wrap gap-1 p-0">
        {row.products.map((key) => (
          <li key={key}>
            <Badge tone="neutral" variant="soft">
              {productLabel(key)}
            </Badge>
          </li>
        ))}
      </ul>
    );

  const companyCell = (entry: CompanyEntry) => (
    <TextLink to={`/companies/${entry.company.id}`} className="font-medium break-words">
      {entry.company.name}
    </TextLink>
  );

  const seedCell = (entry: CompanyEntry) => {
    if (entry.mapped.length > 0) {
      return (
        <ul className="m-0 grid list-none gap-2 p-0">
          {entry.mapped.map((row) => (
            <li key={row.platformCompanyId} className="grid gap-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                {platformName(row)}
                {canEdit && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="max-sm:h-11"
                    aria-label={`Unmap ${row.name} from ${entry.company.name}`}
                    onClick={() => {
                      setUnlinkError(null);
                      setUnlinkTarget(row);
                    }}
                  >
                    Unmap
                  </Button>
                )}
              </div>
              {productsOf(row)}
            </li>
          ))}
        </ul>
      );
    }
    if (entry.suggested.length === 0) return <span className="text-fg-muted">—</span>;
    return (
      <ul className="m-0 grid list-none gap-2 p-0">
        {entry.suggested.map((suggestion, index) => (
          <li key={suggestion.row.platformCompanyId} className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {platformName(suggestion.row)}
            <Badge tone={suggestion.reason === 'domain' ? 'success' : 'neutral'} variant="soft">
              {suggestion.reason === 'domain' ? 'Domain match' : 'Name match'}
            </Badge>
            {canEdit && (
              <Button
                size="sm"
                variant={index === 0 ? 'primary' : 'secondary'}
                className="max-sm:h-11"
                aria-label={`Map ${suggestion.row.name} to ${entry.company.name}`}
                onClick={() =>
                  setLinkTarget({
                    mode: 'choose-company',
                    platform: { platformCompanyId: suggestion.row.platformCompanyId, name: suggestion.row.name },
                    company: { id: entry.company.id, name: entry.company.name },
                  })
                }
              >
                Map
              </Button>
            )}
          </li>
        ))}
      </ul>
    );
  };

  const entryActions = (entry: CompanyEntry) =>
    canEdit ? (
      <Button
        size="sm"
        variant="secondary"
        className="max-sm:h-11"
        aria-label={`Map a SEED company to ${entry.company.name}`}
        onClick={() =>
          setLinkTarget({ mode: 'choose-platform', company: { id: entry.company.id, name: entry.company.name } })
        }
      >
        Add SEED company
      </Button>
    ) : null;

  const seedActions = (row: PlatformCompanyRow) =>
    canEdit ? (
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="secondary"
          className="max-sm:h-11"
          aria-label={`Map ${row.name} to a SEED EMERGE company`}
          onClick={() =>
            setLinkTarget({
              mode: 'choose-company',
              platform: { platformCompanyId: row.platformCompanyId, name: row.name },
              company: null,
            })
          }
        >
          Map to a company
        </Button>
        {canCreateCompany && (
          <Button
            size="sm"
            variant="ghost"
            className="max-sm:h-11"
            loading={preparing === row.platformCompanyId}
            disabled={preparing !== null || linkingCreated}
            aria-label={`Create a company for ${row.name}`}
            onClick={() => void startCreate(row)}
          >
            Create company
          </Button>
        )}
      </div>
    ) : null;

  const emptyState = () => {
    if (needle !== '') {
      return (
        <PiEmpty
          title={`Nothing matches “${search.trim()}”.`}
          action={
            <Button size="sm" variant="secondary" onClick={() => changeSearch('')}>
              Clear search
            </Button>
          }
        />
      );
    }
    if (tab === 'suggested') {
      return (
        <PiEmpty
          title="No suggested matches waiting."
          action={
            <Button size="sm" variant="secondary" onClick={() => setTab('mapped')}>
              Show mapped companies
            </Button>
          }
        />
      );
    }
    if (tab === 'mapped') {
      return (
        <PiEmpty
          title="No company is mapped yet."
          action={
            <Button size="sm" variant="secondary" onClick={() => setTab('suggested')}>
              Show suggested matches
            </Button>
          }
        />
      );
    }
    if (tab === 'seed') return <PiEmpty title="Every SEED company is mapped." />;
    return <PiEmpty title="No companies" />;
  };

  const tabs = [
    { key: 'suggested', label: 'Suggested matches', count: ready ? suggestedEntries.length : null },
    { key: 'mapped', label: 'Mapped', count: ready ? mappedEntries.length : null },
    { key: 'all', label: 'All companies', count: ready ? entries.length : null },
    { key: 'seed', label: 'Unmapped SEED companies', count: ready ? unmappedRows.length : null },
  ] as const;

  const failedSources = load.status === 'ready' ? load.failedSources : [];
  const sortHeader = (label: string) => (
    <PiSortHeader label={label} active direction={sortDir} onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))} />
  );

  return (
    <div className="grid min-w-0 gap-3">
      <PiToolbar>
        <PiSearch value={search} onChange={changeSearch} label="Search companies" className="w-full sm:w-72" />
        {ready && entries.length > 0 && (
          <span className="text-sm whitespace-nowrap text-fg">
            <span className="font-medium tabular-nums">{piCount(mappedEntries.length)}</span>
            {' of '}
            <span className="tabular-nums">{piCount(entries.length)}</span>
            {' companies mapped'}
          </span>
        )}
      </PiToolbar>

      <PiTabs tabs={tabs} value={tab} onChange={setTab} ariaLabel="Mapping status" />

      {failedSources.length > 0 && (
        <Notice tone="warning" role="status">
          {`Some SEED platform sources did not respond, so this list may be incomplete. Not read: ${productList(failedSources)}.`}
        </Notice>
      )}

      {linkingCreated && (
        <Notice tone="info" role="status">
          Mapping the new company…
        </Notice>
      )}

      {outcome !== null && (
        <Notice
          tone={outcome.tone}
          role="status"
          action={
            <div className="flex flex-wrap gap-2">
              {outcome.tone === 'warning' && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => linkCreated(outcome.retry.row, outcome.retry.company)}
                >
                  Retry
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => setOutcome(null)}>
                Dismiss
              </Button>
            </div>
          }
        >
          {outcome.text}
        </Notice>
      )}

      {load.status === 'error' ? (
        <PiError message={load.message} onRetry={() => setReloadToken((n) => n + 1)} />
      ) : load.status === 'loading' ? (
        <PiLoading variant="table" rows={6} />
      ) : visibleCount === 0 ? (
        emptyState()
      ) : tab === 'seed' ? (
        <>
          <PiCardList
            items={shownSeed}
            getKey={(row) => row.platformCompanyId}
            render={(row) => (
              <div className="grid min-w-0 gap-2">
                {platformName(row)}
                {platformFacts(row) !== '' && <div className="text-xs break-words text-fg-muted">{platformFacts(row)}</div>}
                {productsOf(row) !== null && <PiCardField label="Products" value={productsOf(row)} />}
                <div>{seedActions(row)}</div>
              </div>
            )}
          />
          <div className="hidden md:block">
            <TableWrap frozenHeader>
              <Table>
                <THead>
                  <TR>
                    <TH frozen="left">{sortHeader('SEED company')}</TH>
                    <TH>Products</TH>
                    {canEdit && (
                      <TH>
                        <span className="sr-only">Actions</span>
                      </TH>
                    )}
                  </TR>
                </THead>
                <TBody>
                  {shownSeed.map((row) => (
                    <TR key={row.platformCompanyId}>
                      <TD frozen="left" verticalAlign="top" className="min-w-[16rem]">
                        {platformName(row)}
                        {platformFacts(row) !== '' && (
                          <div className="text-xs break-words text-fg-muted">{platformFacts(row)}</div>
                        )}
                      </TD>
                      <TD verticalAlign="top" className="min-w-[10rem]">
                        {productsOf(row) ?? <span className="text-fg-muted">—</span>}
                      </TD>
                      {canEdit && <TD verticalAlign="top">{seedActions(row)}</TD>}
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
          </div>
        </>
      ) : (
        <>
          <PiCardList
            items={shownEntries}
            getKey={(entry) => String(entry.company.id)}
            render={(entry) => (
              <div className="grid min-w-0 gap-2">
                {companyCell(entry)}
                <PiCardField label={entry.mapped.length > 0 ? 'SEED companies' : 'Suggested'} value={seedCell(entry)} />
                <div>{entryActions(entry)}</div>
              </div>
            )}
          />
          <div className="hidden md:block">
            <TableWrap frozenHeader>
              <Table>
                <THead>
                  <TR>
                    <TH frozen="left">{sortHeader('SEED EMERGE company')}</TH>
                    <TH>{tab === 'suggested' ? 'Suggested SEED companies' : 'SEED companies'}</TH>
                    {canEdit && (
                      <TH>
                        <span className="sr-only">Actions</span>
                      </TH>
                    )}
                  </TR>
                </THead>
                <TBody>
                  {shownEntries.map((entry) => (
                    <TR key={entry.company.id}>
                      <TD frozen="left" verticalAlign="top" className="min-w-[14rem]">
                        {companyCell(entry)}
                      </TD>
                      <TD verticalAlign="top" className="min-w-[20rem]">
                        {seedCell(entry)}
                      </TD>
                      {canEdit && <TD verticalAlign="top">{entryActions(entry)}</TD>}
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
          </div>
        </>
      )}

      {ready && visibleCount > limit && (
        <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-fg-muted">
          <span className="tabular-nums">{`${piCount(Math.min(limit, visibleCount))} of ${piCount(visibleCount)}`}</span>
          <Button size="sm" variant="secondary" className="max-sm:h-11" onClick={() => setLimit((n) => n + PAGE_STEP)}>
            Show more
          </Button>
        </div>
      )}

      <LinkPlatformCompanyDialog
        target={linkTarget}
        onClose={() => setLinkTarget(null)}
        onLinked={({ row }) => replaceRow(row)}
      />

      <ConfirmDialog
        open={unlinkTarget !== null}
        onClose={() => {
          if (!unlinkBusy) setUnlinkTarget(null);
        }}
        onConfirm={confirmUnlink}
        title="Unmap SEED company"
        description={
          unlinkTarget !== null && unlinkTarget.link !== null ? (
            <>
              Unmap <strong>{unlinkTarget.name}</strong> from <strong>{unlinkTarget.link.company.name}</strong>? Its
              SEED platform figures stop showing on that company.
            </>
          ) : null
        }
        confirmLabel="Unmap"
        tone="danger"
        loading={unlinkBusy}
        error={unlinkError}
      />

      {canCreateCompany && createFor !== null && createPrefill !== null && (
        <CompanyFormModal
          open
          prefill={createPrefill}
          onClose={() => {
            setCreateFor(null);
            setCreatePrefill(null);
          }}
          onSaved={(company) => {
            const row = createFor;
            if (row !== null) linkCreated(row, company);
          }}
        />
      )}
    </div>
  );
}
